import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export const ServiceAgreement = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-16">
      <Helmet>
        <title>Service Agreement | VortexWebCo</title>
        <meta name="description" content="Service Agreement for Vortex Web Co - Terms and conditions for our web design services." />
        <meta property="og:title" content="Service Agreement | VortexWebCo" />
        <meta property="og:description" content="Service Agreement for Vortex Web Co - Terms and conditions for our web design services." />
        <meta property="og:url" content="https://vortexweb.co/service-agreement" />
        <meta property="og:site_name" content="vortexweb.co" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#e62020] transition-colors font-mono text-sm"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <FileText size={20} className="text-[#e62020]" />
            <span className="text-[#e62020] font-mono text-sm tracking-[0.3em] uppercase">
              // LEGAL
            </span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
            data-testid="service-agreement-title"
          >
            Service <span className="text-[#e62020]">Agreement</span>
          </h1>
          <p className="text-gray-500 font-mono text-sm">
            Vortex Web Co | vortexweb.co | Effective upon purchase
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Intro */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <p className="text-gray-400 leading-relaxed">
              Vortex Web Co provides web design services. By purchasing a plan, the client agrees to the following terms and conditions.
            </p>
          </section>

          {/* Payment Terms */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">01</span>
              Payment Terms
            </h2>
            <p className="text-gray-400 mb-4">The client agrees to pay the selected plan fee:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 text-center">
                <p className="text-[#e62020] font-bold text-2xl" style={{ fontFamily: 'Anton, sans-serif' }}>$125</p>
                <p className="text-gray-500 text-xs font-mono">Monthly</p>
              </div>
              <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 text-center">
                <p className="text-[#e62020] font-bold text-2xl" style={{ fontFamily: 'Anton, sans-serif' }}>$350</p>
                <p className="text-gray-500 text-xs font-mono">Quarterly</p>
              </div>
              <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 text-center">
                <p className="text-[#e62020] font-bold text-2xl" style={{ fontFamily: 'Anton, sans-serif' }}>$650</p>
                <p className="text-gray-500 text-xs font-mono">6-Month</p>
              </div>
              <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 text-center">
                <p className="text-[#e62020] font-bold text-2xl" style={{ fontFamily: 'Anton, sans-serif' }}>$1,200</p>
                <p className="text-gray-500 text-xs font-mono">Annual</p>
              </div>
            </div>
          </section>

          {/* Client Responsibilities */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">02</span>
              Client Responsibilities
            </h2>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Provide content and feedback within <strong className="text-white">7 days</strong> of requests</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Allow Vortex Web Co to display completed work in our portfolio</span>
              </li>
            </ul>
          </section>

          {/* Revisions */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">03</span>
              Revisions
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Revisions are limited to the amount included in each plan. Additional revisions may be purchased as add-ons.
            </p>
          </section>

          {/* Ownership Rights */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">04</span>
              Ownership Rights
            </h2>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Upon full payment, the client owns their final website</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Vortex Web Co retains rights to underlying code and templates</span>
              </li>
            </ul>
          </section>

          {/* Cancellation */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">05</span>
              Cancellation Policy
            </h2>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>Either party may cancel with <strong className="text-white">14 days notice</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#e62020] mt-1">›</span>
                <span>All work completed to date must be paid upon cancellation</span>
              </li>
            </ul>
          </section>

          {/* Governing Law */}
          <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">06</span>
              Governing Law
            </h2>
            <p className="text-gray-400 leading-relaxed">
              This agreement is governed by the laws of <strong className="text-white">Texas, United States</strong>.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-[#0d0d0d] border border-[#e62020]/20 p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
              <span className="text-[#e62020] font-mono text-sm">07</span>
              Contact
            </h2>
            <div className="font-mono text-sm">
              <p className="text-white mb-1">Vortex Web Co</p>
              <p className="text-gray-500">Website: <a href="https://vortexweb.co" className="text-[#e62020] hover:underline">vortexweb.co</a></p>
              <p className="text-gray-500">Email: <a href="mailto:vortexwebco@gmail.com" className="text-[#e62020] hover:underline">vortexwebco@gmail.com</a></p>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};
